import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, User as UserIcon, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { CartDrawer } from "@/components/CartDrawer";
import api from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const useContent = () => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/content").then(({ data }) => setC(data)); }, []);
  return c;
};

const NavItem = ({ to, children, end, testid }) => (
  <NavLink to={to} end={end} data-testid={testid}
    className={({ isActive }) => `relative font-semibold text-[15px] transition-colors ${isActive ? "text-[var(--ink)]" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}>
    {children}
  </NavLink>
);

export const Header = () => {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const loc = useLocation();
  const c = useContent();
  const nav = c?.nav || [{ label: "Home", to: "/" }, { label: "Shop", to: "/shop" }];
  const brand = c?.brand || { name_left: "Nailedit", name_right: "Beauty" };

  return (
    <header className="sticky top-0 z-50 nav-glass">
      <div className="max-w-[1180px] mx-auto px-6 flex items-center justify-between h-[68px]">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 text-[var(--ink)]">
          <img src="/logo.png" alt={`${brand.name_left} ${brand.name_right}`} className="w-10 h-10 rounded-full object-cover" />
          <span className="font-serif font-semibold text-[1.4rem] tracking-tight hidden sm:inline">
            {brand.name_left} <em className="font-normal" style={{ color: "var(--bloom)" }}>{brand.name_right}</em>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {nav.map((n, i) => (
            <NavItem key={i} to={n.to} end={n.to === "/"} testid={`nav-${i}`}>{n.label}</NavItem>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="w-9 h-9 rounded-full bg-white shadow-[0_4px_14px_-6px_rgba(38,12,24,.25)] flex items-center justify-center hover:-translate-y-0.5 transition">
                {user.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <UserIcon className="w-4 h-4 text-[var(--ink)]" />}
              </DropdownMenuTrigger>
              <DropdownMenuContent className="rounded-2xl bg-white border-[#fde0e8] shadow-xl mt-2">
                <div className="px-3 py-2 text-sm">
                  <div className="font-semibold text-[var(--ink)]">{user.name}</div>
                  <div className="text-xs text-[var(--ink-soft)]">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/account" data-testid="menu-account"><UserIcon className="w-4 h-4 mr-2" /> My orders</Link></DropdownMenuItem>
                {user.role === "admin" && <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin"><LayoutDashboard className="w-4 h-4 mr-2" /> Admin</Link></DropdownMenuItem>}
                <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to={`/login?next=${encodeURIComponent(loc.pathname)}`} data-testid="login-link" className="hidden sm:inline-flex text-[15px] font-semibold text-[var(--ink-soft)] hover:text-[var(--ink)] transition">Sign in</Link>
          )}
          <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid="open-cart-button"
            className="relative flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-[0_4px_14px_-6px_rgba(38,12,24,.25)] font-bold text-[var(--ink)] text-sm hover:-translate-y-0.5 transition">
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">Bag</span>
            <span data-testid="cart-count-badge" className="text-white text-[11px] font-extrabold min-w-[20px] h-5 rounded-full grid place-items-center px-1.5" style={{ background: "var(--bloom)" }}>{count}</span>
          </button>
        </div>
      </div>
      <CartDrawer />
    </header>
  );
};

export const Footer = () => {
  const c = useContent();
  const brand = c?.brand || {};
  const shop = c?.footer_shop || { title: "Shop", links: [] };
  const hello = c?.footer_hello || { title: "Hello", line1: "", line2: "" };
  const socials = (c?.footer_socials || []).map((s) =>
    typeof s === "string" ? { label: s, url: "" } : s
  );
  return (
    <footer className="foot mt-24">
      <div className="max-w-[1180px] mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="" className="w-14 h-14 rounded-full object-cover" />
            <div className="font-serif font-semibold text-[1.8rem] tracking-tight">
              {brand.name_left} <em className="font-normal" style={{ color: "var(--bloom)" }}>{brand.name_right}</em>
            </div>
          </div>
          <p className="mt-4 text-white/70 max-w-md leading-relaxed">{brand.footer_tagline}</p>
          <div className="mt-6 flex gap-3 flex-wrap">
            {socials.map((s, i) => {
              const hasUrl = s.url && s.url.trim();
              return (
                <a
                  key={s.label + i}
                  href={hasUrl ? s.url : "#"}
                  target={hasUrl ? "_blank" : undefined}
                  rel={hasUrl ? "noopener noreferrer" : undefined}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="text-xs font-bold uppercase tracking-[0.18em] px-4 py-2 rounded-full border border-white/20 hover:border-white/60 transition"
                >
                  {s.label}
                </a>
              );
            })}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[var(--bloom)] mb-4">{shop.title}</div>
          <ul className="space-y-2.5">
            {(shop.links || []).map((l, i) => <li key={i}><Link to={l.to}>{l.label}</Link></li>)}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[var(--bloom)] mb-4">{hello.title}</div>
          <ul className="space-y-2.5">
            <li>{hello.line1}</li>
            <li>{hello.line2}</li>
            <li><Link to="/account">My account</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1180px] mx-auto px-6 py-6 text-xs text-white/50 flex flex-wrap justify-between gap-3">
          <span>{brand.footer_copyright}</span>
          <span>Privacy · Terms · Shipping</span>
        </div>
      </div>
    </footer>
  );
};
